/**
 * Patient data processing, statistics calculation, and risk assessment
 */
import { formatAddress, getFullAddress, formatPatientName, formatStatusDisplay, calculateAge } from './formatters';
import { getEmptyPatientStats } from './emptyStates';

/**
 * Format patient data for display, properly handling nested objects
 * @param {Object} patientData - Raw patient data from API
 * @returns {Object} - Formatted patient data
 */
export const formatPatientData = (patientData) => {
  if (!patientData) return null;

  // Handle both patient data and queue data structures
  const isQueueData = 'queueStatus' in patientData || 'eventId' in patientData;
  
  let eventInfo, locationInfo, queueStatus, checkInTime;
  
  if (isQueueData) {
    eventInfo = patientData.eventId;
    locationInfo = eventInfo?.location;
    queueStatus = patientData.queueStatus || patientData.status;
    checkInTime = patientData.checkInTime;
  } else {
    eventInfo = patientData.queueInfo?.eventId;
    locationInfo = eventInfo?.location;
    queueStatus = patientData.queueInfo?.status;
    checkInTime = patientData.queueInfo?.checkInTime;
  }

  return {
    ...patientData,
    // Format event display - handle null case
    eventDisplay: eventInfo ? {
      title: eventInfo.title || 'Unknown Event',
      date: eventInfo.startDate ? new Date(eventInfo.startDate).toLocaleDateString() : 'No date',
      id: eventInfo.id || eventInfo._id
    } : {
      title: 'No Event Assigned',
      date: 'No date',
      id: null
    },
    
    // Format location display - handle null case
    locationDisplay: locationInfo ? {
      name: locationInfo.name || 'Unknown Location',
      address: formatAddress(locationInfo.address),
      fullAddress: getFullAddress(locationInfo.address)
    } : {
      name: 'No Location',
      address: 'No address available',
      fullAddress: 'No address'
    },
    
    // Format queue status
    queueStatus: queueStatus || 'unknown',
    checkInTime: checkInTime ? 
      new Date(checkInTime).toLocaleString() : null,
    
    // Add formatted fields for display
    patientName: formatPatientName(patientData),
    eventTitle: eventInfo?.title || 'No Event Assigned',
    locationName: locationInfo?.name || 'No Location',
    statusDisplay: formatStatusDisplay(queueStatus)
  };
};

/**
 * Process and format patient data for analytics display
 * @param {Array} patients - Array of patient data
 * @returns {Array} - Processed patient data
 */
export const processPatientDataForDisplay = (patients) => {
  if (!Array.isArray(patients)) return [];
  
  return patients.map(patient => formatPatientData(patient)).filter(Boolean);
};

/**
 * Extract event statistics from patient data
 * @param {Array} patients - Array of patient data
 * @returns {Object} - Event statistics
 */
export const extractEventStatistics = (patients) => {
  if (!Array.isArray(patients)) return { events: [], locations: [] };
  
  const eventMap = new Map();
  const locationMap = new Map();
  
  patients.forEach(patient => {
    const eventInfo = patient.queueInfo?.eventId;
    const locationInfo = eventInfo?.location;
    
    // Track events
    if (eventInfo) {
      const eventId = eventInfo.id || eventInfo._id;
      if (eventId && !eventMap.has(eventId)) {
        eventMap.set(eventId, {
          id: eventId,
          title: eventInfo.title || 'Unknown Event',
          date: eventInfo.startDate,
          patientCount: 0
        });
      }
      if (eventId) {
        eventMap.get(eventId).patientCount++;
      }
    }
    
    // Track locations
    if (locationInfo) {
      const locationKey = locationInfo.name || 'Unknown';
      if (!locationMap.has(locationKey)) {
        locationMap.set(locationKey, {
          name: locationInfo.name || 'Unknown Location',
          address: formatAddress(locationInfo.address),
          eventCount: 0,
          patientCount: 0
        });
      }
      const location = locationMap.get(locationKey);
      location.patientCount++;
      if (eventInfo) location.eventCount++;
    }
  });
  
  return {
    events: Array.from(eventMap.values()),
    locations: Array.from(locationMap.values())
  };
};

/**
 * Assess patient risk level based on medical history and data
 * @param {Object} patient - Patient data
 * @returns {string} - Risk level: 'low', 'medium', 'high', 'critical'
 */
export const assessPatientRisk = (patient) => {
  let riskScore = 0;
  const medicalHistory = patient.medicalHistory || {};

  // High-risk conditions
  if (medicalHistory.priorHeartAttack) riskScore += 3;
  if (medicalHistory.priorStroke) riskScore += 3;
  if (medicalHistory.heartFailure) riskScore += 3;
  if (medicalHistory.diabetes) riskScore += 2;
  if (medicalHistory.hypertension) riskScore += 2;
  if (medicalHistory.highCholesterol) riskScore += 1;

  // Family history
  if (medicalHistory.familyHistoryHeartDisease) riskScore += 1;
  if (medicalHistory.familyHistoryEarlyHeartDisease) riskScore += 2;
  if (medicalHistory.familyHistoryStroke) riskScore += 1;

  // Age factor
  const age = calculateAge(patient.dateOfBirth);
  if (age > 65) riskScore += 2;
  else if (age > 55) riskScore += 1;

  // Determine risk level
  if (riskScore >= 7) return 'critical';
  if (riskScore >= 4) return 'high';
  if (riskScore >= 2) return 'medium';
  return 'low';
};

/**
 * Calculate patient statistics from processed patient data
 * @param {Array} patients - Processed patient data
 * @returns {Object} - Calculated statistics
 */
export const calculatePatientStats = (patients) => {
  if (!Array.isArray(patients) || patients.length === 0) {
    return getEmptyPatientStats();
  }

  const stats = {
    total: patients.length,
    new: 0,
    returning: 0,
    pending: 0,
    completed: 0,
    needsAttention: 0,
    byAge: {
      '18-30': 0,
      '31-45': 0,
      '46-60': 0,
      '61+': 0
    },
    byGender: {
      male: 0,
      female: 0,
      other: 0,
      unknown: 0
    },
    byRisk: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    }
  };

  patients.forEach(patient => {
    // Count new vs returning patients
    if (patient.usedBefore) {
      stats.returning++;
    } else {
      stats.new++;
    }

    // Count by queue status
    const status = patient.queueStatus?.toLowerCase();
    switch (status) {
      case 'pending_review':
      case 'new_patient':
        stats.pending++;
        break;
      case 'completed':
      case 'archived':
        stats.completed++;
        break;
      case 'needs_attention':
        stats.needsAttention++;
        break;
      default:
        break;
    }

    // Calculate age and categorize
    if (patient.dateOfBirth) {
      const age = calculateAge(patient.dateOfBirth);
      if (age >= 18 && age <= 30) stats.byAge['18-30']++;
      else if (age >= 31 && age <= 45) stats.byAge['31-45']++;
      else if (age >= 46 && age <= 60) stats.byAge['46-60']++;
      else if (age > 60) stats.byAge['61+']++;
    }

    // Count by gender
    const gender = patient.birthGender?.toLowerCase() || 'unknown';
    if (Object.prototype.hasOwnProperty.call(stats.byGender, gender)) {
      stats.byGender[gender]++;
    } else {
      stats.byGender.unknown++;
    }

    // Risk assessment
    const riskLevel = assessPatientRisk(patient);
    if (Object.prototype.hasOwnProperty.call(stats.byRisk, riskLevel)) {
      stats.byRisk[riskLevel]++;
    }
  });

  return stats;
};
